import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/api';
import toast from 'react-hot-toast';
import { Draft, Team } from '../types';
import { 
  Container, 
  Card, 
  Button, 
  LoadingSpinner,
  colors,
  spacing,
  typography,
  media,
  shadows
} from '../styles/GlobalStyles';
import { 
  Trophy, 
  Users, 
  Play, 
  CheckCircle,
  Plus,
  BarChart3
} from 'lucide-react';

const DashboardContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: #000000;
`;

const WelcomeSection = styled(Card)`
  margin-bottom: 2rem;
  background: linear-gradient(175deg, #111827 0%, #000 100%);
  border: 1px solid #00FF4140;
`;

const WelcomeContent = styled.div`
  text-align: center;
`;

const WelcomeTitle = styled.h1`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[2]};
`;

const WelcomeSubtitle = styled.p`
  font-size: ${typography.fontSize.lg};
  color: ${colors.grey[300]};
  margin-bottom: ${spacing[6]};
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${spacing[4]};
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[6]};
  margin-bottom: ${spacing[8]};

  ${media.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: ${spacing[6]};
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${shadows.lg};
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${colors.green}20;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing[4]} auto;
  color: ${colors.green};
`;

const StatValue = styled.div`
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[6]};

  ${media.desktop} {
    grid-template-columns: 2fr 1fr;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[4]};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const DraftsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const DraftItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  padding: ${spacing[4]};
  background: ${colors.grey[950]};
  border: 1px solid ${colors.grey[800]};
  border-radius: ${spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.grey[900]};
    border-color: ${colors.grey[700]};
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing[3]};
  }
`;

const DraftInfo = styled.div`
  flex: 1;
`;

const DraftName = styled.div`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
`;

const DraftDetails = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

const DraftStatus = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'status'
})<{ status: string }>`
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: ${colors.success}20;
          color: ${colors.success};
        `;
      case 'completed':
        return `
          background: ${colors.info}20;
          color: ${colors.info};
        `;
      default:
        return `
          background: ${colors.grey[700]};
          color: ${colors.grey[300]};
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing[8]} ${spacing[4]};
  color: ${colors.grey[400]};
`;

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [draftsResponse, teamsResponse] = await Promise.all([
        apiClient.drafts.getAll(),
        apiClient.teams.getAll()
      ]);

      if (draftsResponse.success) {
        setDrafts(draftsResponse.data.drafts);
      }
      if (teamsResponse.success) {
        setTeams(teamsResponse.data.teams);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeDrafts = drafts.filter(draft => draft.status === 'active');
  const createdDrafts = drafts.filter(draft => draft.status === 'created');
  const completedDrafts = drafts.filter(draft => draft.status === 'completed');
  const userTeam = teams.find(team => team.ownerId?._id === user?._id);

  const handleJoinDraft = () => {
    if (activeDrafts.length > 0) {
      navigate(`/draft/${activeDrafts[0]._id}`);
      return;
    }

    if (createdDrafts.length > 0) {
      navigate(`/draft/${createdDrafts[0]._id}`);
      toast('Draft is created but not started yet.', { icon: '⏳' });
      return;
    }

    toast.error('No active draft available to join right now.');
  };

  if (loading) {
    return (
      <DashboardContainer>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <LoadingSpinner size="lg" />
          </div>
        </Container>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Container>
        <WelcomeSection>
          <WelcomeContent>
            <WelcomeTitle>Welcome back, {user?.username}!</WelcomeTitle>
            <WelcomeSubtitle>
              Ready to dominate your fantasy football draft?
            </WelcomeSubtitle>
            
            <QuickActions>
              <Button 
                onClick={handleJoinDraft}
                size="lg"
              >
                <Play size={20} />
                Join Draft
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/teams')}
                size="lg"
              >
                <Users size={20} />
                View Teams
              </Button>
              {user?.role === 'admin' && (
                <Button 
                  variant="secondary" 
                  onClick={() => navigate('/admin')}
                  size="lg"
                >
                  <Plus size={20} />
                  Create Draft
                </Button>
              )}
            </QuickActions>
          </WelcomeContent>
        </WelcomeSection>

        <StatsGrid>
          <StatCard>
            <StatIcon>
              <Trophy />
            </StatIcon>
            <StatValue>{drafts.length}</StatValue>
            <StatLabel>Total Drafts</StatLabel>
          </StatCard>

          <StatCard>
            <StatIcon>
              <Play />
            </StatIcon>
            <StatValue>{activeDrafts.length}</StatValue>
            <StatLabel>Active Drafts</StatLabel>
          </StatCard>

          <StatCard>
            <StatIcon>
              <CheckCircle />
            </StatIcon>
            <StatValue>{completedDrafts.length}</StatValue>
            <StatLabel>Completed</StatLabel>
          </StatCard>

          <StatCard>
            <StatIcon>
              <Users />
            </StatIcon>
            <StatValue>{teams.length}</StatValue>
            <StatLabel>Teams</StatLabel>
          </StatCard>
        </StatsGrid>

        <SectionGrid>
          <Card>
            <SectionTitle>
              <Trophy size={24} />
              Recent Drafts
            </SectionTitle>
            
            {drafts.length > 0 ? (
              <DraftsList>
                {drafts.slice(0, 5).map(draft => (
                  <DraftItem key={draft._id}>
                    <DraftInfo>
                      <DraftName>{draft.name}</DraftName>
                      <DraftDetails>
                        {draft.teams.length} teams • Round {draft.currentRound}
                      </DraftDetails>
                    </DraftInfo>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3] }}>
                      <DraftStatus status={draft.status}>
                        {draft.status}
                      </DraftStatus>
                      
                      {draft.status === 'active' && (
                        <Button 
                          size="sm"
                          onClick={() => navigate(`/draft/${draft._id}`)}
                        >
                          Join
                        </Button>
                      )}
                      {draft.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => navigate(`/draft/${draft._id}/results`)}
                        >
                          View Results
                        </Button>
                      )}
                    </div>
                  </DraftItem>
                ))}
              </DraftsList>
            ) : (
              <EmptyState>
                <Trophy size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
                <p>No drafts available</p>
                <p style={{ fontSize: typography.fontSize.sm }}>
                  {user?.role === 'admin' ? 'Create your first draft to get started' : 'Wait for an admin to create a draft'}
                </p>
              </EmptyState>
            )}
          </Card>

          <Card>
            <SectionTitle>
              <BarChart3 size={24} />
              Your Team
            </SectionTitle>
            
            {userTeam ? (
              <div>
                <div style={{ marginBottom: spacing[4] }}>
                  <h3 style={{ color: colors.white, marginBottom: spacing[2] }}>
                    {userTeam.name}
                  </h3>
                  <p style={{ color: colors.grey[400], fontSize: typography.fontSize.sm }}>
                    Team #{userTeam.teamNumber}
                  </p>
                </div>

                <div style={{ display: 'flex', justifyContent: 'between', marginBottom: spacing[4] }}>
                  <div>
                    <div style={{ color: colors.white, fontWeight: typography.fontWeight.semibold }}>
                      {userTeam.players.length}
                    </div>
                    <div style={{ color: colors.grey[400], fontSize: typography.fontSize.sm }}>
                      Players
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: colors.green, fontWeight: typography.fontWeight.semibold }}>
                      ${userTeam.budget.remaining}
                    </div>
                    <div style={{ color: colors.grey[400], fontSize: typography.fontSize.sm }}>
                      Remaining
                    </div>
                  </div>
                </div>

                <Button 
                  variant="secondary" 
                  fullWidth
                  onClick={() => navigate('/teams')}
                >
                  View Team Details
                </Button>
              </div>
            ) : (
              <EmptyState>
                <Users size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
                <p>No team assigned</p>
                <p style={{ fontSize: typography.fontSize.sm }}>
                  Contact an admin to assign you to a team
                </p>
              </EmptyState>
            )}
          </Card>
        </SectionGrid>
      </Container>
    </DashboardContainer>
  );
};

export default DashboardPage;
